import Authenticating from '@/features/shared/Authenticating';
import { useLog } from '@/features/shared/Hooks/useLog';
import { useMounted } from '@/hooks/useMounted';
import { isUrlInDomainList } from '@/hooks/useRequiredInterceptor';
import {
  CMS_BASE_URL,
  SSO_RESOURCEAPI_SSO_FORBIDDEN,
  SSO_RESOURCEAPI_SSO_UNAUTHORIZED,
  SSO_RESOURCEAPI_SSO_PENDING_APPROVAL,
} from '@/utils/constants';
import { StatusCodes } from '@/utils/constants/http-codes';
import { Backdrop, Fade, Modal } from '@material-ui/core';
import axios, { AxiosError } from 'axios';
import React from 'react';
import { useStorageState } from 'react-storage-hooks';
import { useOidc } from '../Oidc';
import { usePowerApps } from '../PowerApps';
import { useAuthErrorInterceptor } from './useAuthErrorInterceptor';
import { useAuthHeaderInterceptor } from './useAuthHeaderInterceptor';
import { useAuthManager } from './useAuthManager';

const AUTHORIZED_DOMAINS = [`${CMS_BASE_URL}/resourceapi/portal`];

/**
 * Domain to pass for the authentication flow, use ! for domains dont need authorization
 */
const TRUSTED_DOMAINS = [
  `${CMS_BASE_URL}/resourceapi/portal`,
  `${CMS_BASE_URL}/restservices/portal`,
  `!${CMS_BASE_URL}/restservices/portal/authorize`,
];

type AuthContextProps = {
  isAuthorizing: boolean;
  isAuthorized: boolean | null;
  isPendingApproval: boolean | null;
  isForbidden: boolean;
  authToken: string | null;
  resetToken: () => void;
  killToken: () => void;
  logIn: () => void;
  logOut: () => void;
  lastPageBeforeLogout: string | null;
  removeSessionUser: () => Promise<void>;
};

export const AuthContext = React.createContext<AuthContextProps>({
  isAuthorizing: false,
  isAuthorized: null,
  isPendingApproval: null,
  isForbidden: false,
  authToken: null,
  resetToken: () => {},
  killToken: () => {},
  logIn: () => {},
  logOut: () => {},
  lastPageBeforeLogout: '',
  removeSessionUser: () => Promise.resolve(),
});

export const AuthProvider = ({ children }: React.PropsWithChildren<any>) => {
  const isMounted = useMounted();
  const {
    isAuthorizing,
    isAuthorized,
    isPendingApproval,
    isForbidden,
    authToken,
    requestAuthorization,
    resetAuthorization,
    // error,
  } = useAuthManager();
  const { signIn, signOut, removeSessionUser } = useOidc();
  const { disconnect: disconnectPowerApps } = usePowerApps();
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const [lastPageBeforeLogout, setLastPageBeforeLogout] = useStorageState<string>(
    localStorage,
    'lastPageBeforeLogout',
  );
  const Log = useLog({ context: 'AuthProvider' });

  const handleResetToken = React.useCallback(() => {
    Log('Reset token');
    resetAuthorization();
    Promise.resolve().then(() => {
      window.location.reload();
    });
  }, [Log, resetAuthorization]);

  // Handle Authorization required interceptor
  const handleAuthRequired = React.useCallback(async () => {
    Log('Authorization required');
    requestAuthorization();
  }, [requestAuthorization, Log]);

  // Handle Authorization Response Error
  const handleAuthResponseError = React.useCallback(
    (e: AxiosError): Promise<any> => {
      Log('Authorize response error', e?.response?.status, { e });

      const { status } = e.response || { status: null };

      const handle401Error = () => {
        if (isUrlInDomainList(e.config.url, AUTHORIZED_DOMAINS)) {
          return axios({ ...e.config, url: SSO_RESOURCEAPI_SSO_UNAUTHORIZED });
        }
        return Promise.reject(e);
      };

      const handle403Error = ({ config }: any) => {
        if (isPendingApproval) {
          Log('Portal user is pending approval');
          return axios({ ...config, url: SSO_RESOURCEAPI_SSO_PENDING_APPROVAL });
        }
        // Otherwise get the forbidden information
        return axios({ ...config, url: SSO_RESOURCEAPI_SSO_FORBIDDEN });
      };

      switch (status) {
        case StatusCodes.UNAUTHORIZED:
          return handle401Error();
        case StatusCodes.FORBIDDEN:
          return handle403Error(e);
        default:
          return Promise.reject(e);
      }
    },
    [Log, isPendingApproval],
  );

  // Handle Log In
  const handleLogin = React.useCallback(async () => {
    return signIn();
  }, [signIn]);

  // Handle Log Out
  const handleLogout = React.useCallback(async () => {
    setLastPageBeforeLogout(`${window.location.pathname}${window.location.search}`);
    setIsLoggingOut(true);

    // for not displaying the wrong information before to redirect to the SSO logout
    resetAuthorization();

    try {
      await disconnectPowerApps();
    } catch (e) {
      Log('Error: PowerApps disconnection failed.', e);
    }

    // TODO (Luis Sardon): Pull back once Experlogix implements a logout flow
    // try {
    //   await disconnectExperlogix();
    // } catch (e) {
    //   Log('Error: Experlogix disconnection failed.', e);
    // }

    return signOut();
  }, [disconnectPowerApps, resetAuthorization, setLastPageBeforeLogout, signOut, Log]);

  /**
   * Intercept calls to specific URLs attaching auth headers
   *
   * Should be called before other interceptors
   */
  useAuthHeaderInterceptor({ authToken, trustedDomains: TRUSTED_DOMAINS });

  // Intercept authorization errors
  useAuthErrorInterceptor({
    isAuthorized,
    onAuthRequired: handleAuthRequired,
    onAuthError: handleAuthResponseError,
    trustedDomains: TRUSTED_DOMAINS,
    excludedDomainsForErrors: [`${CMS_BASE_URL}/restservices/portal/pricelist`],
  });

  return (
    <AuthContext.Provider
      value={{
        isAuthorizing,
        isAuthorized,
        isPendingApproval,
        isForbidden,
        authToken,
        resetToken: handleResetToken,
        killToken: resetAuthorization,
        logIn: handleLogin,
        logOut: handleLogout,
        lastPageBeforeLogout,
        removeSessionUser,
      }}
    >
      <Modal
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        open={isAuthorizing || isLoggingOut}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={isAuthorizing || isLoggingOut}>
          <Authenticating />
        </Fade>
      </Modal>
      {isMounted && !isAuthorizing && children}
    </AuthContext.Provider>
  );
};

export const PreviewAuthProvider = ({ children }: React.PropsWithChildren<any>) => (
  <AuthContext.Provider
    value={{
      isAuthorizing: false,
      isAuthorized: true,
      isPendingApproval: false,
      isForbidden: false,
      authToken: '',
      resetToken: () => {},
      killToken: () => {},
      logIn: () => {},
      logOut: () => {},
      lastPageBeforeLogout: '',
      removeSessionUser: () => Promise.resolve(),
    }}
  >
    {children}
  </AuthContext.Provider>
);

export const useAuth = () => {
  const auth = React.useContext(AuthContext);

  if (!auth) {
    throw new Error('You need the AuthProvider');
  }

  return auth;
};
